/**
 * `/contacts/:id` — three-column detail (design §2.5).
 *
 * Left: identity + provenance + enrichment. Centre: the timeline. Right: the
 * action rail.
 *
 * Every panel is wrapped in its own `FeatureBoundary`. Design §2.12: "one dead
 * integration may never take down the CRM" — a timeline that throws must not
 * take the identity card with it, and an enrichment panel that 403s on a
 * suppressed contact must not blank the page.
 *
 * ## `?focus=<interactionId>`
 *
 * A digest citation links here with the interaction it cited. The param is
 * declared on the route (`parseContactDetailSearch`) so it is typed, bookmarkable
 * and validated; `InteractionTimeline` does the scrolling and the ring. See
 * `why-this-card.tsx` for which citations can carry one.
 *
 * ## What a SHARED contact renders instead
 *
 * A row reached through a grant carries `access.source === 'shared'`, a chip
 * naming the owner and the scope, an enrichment panel that explains the
 * per-workspace licensing rather than showing a blank, and — where the grant
 * allows it — an adopt dialog with a field-by-field diff. It has no share button:
 * a grant confers read, not the right to re-grant.
 */

import { Badge, Button, Card, CardContent, CardHeader, CardTitle, cn, SkeletonRows } from '@crm/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  useContactQuery,
  useOrgListQuery,
  useSuppressionsQuery,
  useTimelineQuery,
} from '../../../api/queries.ts';
import { useOrgId } from '../../../api/session.ts';
import type { IncomingShareRow } from '../../../api/sharing.ts';
import { useIncomingSharesQuery } from '../../../api/sharing.ts';
import { ContactTypeBadge } from '../../../components/contacts/contact-type-badge.tsx';
import { EditContactDialog } from '../../../components/contacts/edit-contact-dialog.tsx';
import { EnrichmentPanel } from '../../../components/contacts/enrichment-panel.tsx';
import { deriveFieldSources } from '../../../components/contacts/field-sources.ts';
import { NotWired } from '../../../components/not-wired.tsx';
import { SuppressionControls } from '../../../components/privacy/suppression-controls.tsx';
import { ProvenanceField } from '../../../components/provenance-field.tsx';
import {
  ErrorPanel,
  FeatureBoundary,
  RouteErrorPanel,
} from '../../../components/route-error-boundary.tsx';
import { AdoptDialog } from '../../../components/sharing/adopt-dialog.tsx';
import { ContactSharingPanel } from '../../../components/sharing/contact-sharing-panel.tsx';
import { ShareChip } from '../../../components/sharing/share-chip.tsx';
import { InteractionTimeline } from '../../../components/timeline/interaction-timeline.tsx';
import { formatDate, fullName } from '../../../lib/format.ts';
import { parseContactDetailSearch } from '../../../lib/search.ts';

export const Route = createFileRoute('/_authed/contacts/$contactId')({
  validateSearch: parseContactDetailSearch,
  component: ContactDetailPage,
  errorComponent: RouteErrorPanel,
});

function ContactDetailPage() {
  const { contactId } = Route.useParams();
  const { focus } = Route.useSearch();
  const contact = useContactQuery(contactId);
  const timeline = useTimelineQuery(contactId);
  const [adopting, setAdopting] = useState(false);
  const [editing, setEditing] = useState(false);

  const items = useMemo(
    () => timeline.data?.pages.flatMap((page) => page.items) ?? [],
    [timeline.data],
  );

  const shared = contact.data?.access.source === 'shared';

  /**
   * The inbound grant this row arrived through, needed by the adopt dialog.
   *
   * `contacts.get` returns `access.shareId` but not the grant's other fields
   * (owner org name, `allowAdopt`, `adoptedContactId`), and there is no
   * `shares.get`. So the incoming inventory is read and matched on that id. It
   * is only fetched for a shared row — an owned contact never needs it.
   */
  const incoming = useIncomingSharesQuery({ resourceType: 'contact', enabled: shared });
  const shareId = contact.data?.access.shareId ?? null;
  const grant: IncomingShareRow | null =
    shareId === null ? null : (incoming.data?.items.find((row) => row.id === shareId) ?? null);
  /**
   * True once the inventory has answered and the grant was not in it — the first
   * page holds 50, and there is no per-share read to fall back to. The header
   * then says the grant could not be resolved rather than saying "Loading…"
   * forever.
   */
  const grantUnresolved = shared && grant === null && incoming.isSuccess;

  // Suppression flags for this contact. The list read is org-wide and cached, so
  // this costs nothing extra on a page that already sits behind the same query.
  const suppressions = useSuppressionsQuery(null);
  const suppression =
    suppressions.data?.items.find((entry) => entry.contactId === contactId) ?? null;

  // "Enrich for {ThisOrg}" needs the viewing workspace's NAME, and the session
  // carries only its id. `org.list` is the read that has both, and it is already
  // warm for the tenant switcher.
  const activeOrgId = useOrgId();
  const orgs = useOrgListQuery();
  const viewingOrgName = orgs.data?.items.find((org) => org.id === activeOrgId)?.name ?? null;

  if (contact.isLoading) {
    return <SkeletonRows rows={8} className="p-4" />;
  }

  if (contact.isError || contact.data === undefined) {
    return (
      <ErrorPanel
        error={contact.error}
        route={`/contacts/${contactId}`}
        reset={() => void contact.refetch()}
      />
    );
  }

  const detail = contact.data;
  const sources = deriveFieldSources(detail);
  const name = fullName(detail.firstName, detail.lastName);

  return (
    <div
      className={cn(
        'grid min-h-0 flex-1 grid-cols-1 gap-4 p-4',
        'xl:grid-cols-[20rem_minmax(0,1fr)_18rem]',
      )}
    >
      <FeatureBoundary feature="Identity card">
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">{name}</CardTitle>
                <ContactTypeBadge type={detail.contactType} />
                {detail.access.canEdit ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto"
                    onClick={() => setEditing(true)}
                  >
                    Edit
                  </Button>
                ) : null}
              </div>
              <ShareChip
                source={detail.access.source}
                ownerOrgName={detail.access.ownerOrgName}
                scope={detail.access.scope}
                className="w-fit"
              />
              {shared && detail.access.canAdopt ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-1 w-fit"
                  disabled={grant === null}
                  onClick={() => setAdopting(true)}
                  {...(grantUnresolved
                    ? {
                        title:
                          'The grant is not on the first page of your inbound shares, and there is no per-share read to fall back on. Open Settings → Sharing to find it.',
                      }
                    : {})}
                >
                  {grant !== null
                    ? 'Adopt into this workspace…'
                    : grantUnresolved
                      ? 'Adopt — grant not found'
                      : 'Loading the grant…'}
                </Button>
              ) : null}
            </CardHeader>
            <CardContent className="flex flex-col divide-y divide-border">
              <ProvenanceField
                label="Company"
                value={detail.companyName}
                source={sources.companyName.source}
                capturedAt={sources.companyName.capturedAt}
              />
              <ProvenanceField
                label="Title"
                value={detail.title}
                source={sources.title.source}
                capturedAt={sources.title.capturedAt}
              />
              <ProvenanceField
                label="Email"
                value={detail.primaryEmail}
                source={sources.primaryEmail.source}
                capturedAt={sources.primaryEmail.capturedAt}
                confidence={sources.primaryEmail.confidence}
              />
              <ProvenanceField
                label="Phone"
                value={detail.primaryPhoneE164}
                source={sources.primaryPhone.source}
                capturedAt={sources.primaryPhone.capturedAt}
              />
              <ProvenanceField
                label="LinkedIn"
                value={
                  detail.linkedinUrl === null ? null : (
                    <a
                      href={detail.linkedinUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="underline underline-offset-2"
                    >
                      {detail.linkedinSlug ?? detail.linkedinUrl}
                    </a>
                  )
                }
                source={sources.linkedinUrl.source}
              />
              <ProvenanceField
                label="Met at"
                value={detail.metAtEventName}
                source={null}
                capturedAt={detail.metAt}
              />
            </CardContent>
          </Card>

          {detail.notes === null ? null : (
            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm">{detail.notes}</p>
              </CardContent>
            </Card>
          )}

          <FeatureBoundary feature="Enrichment">
            <EnrichmentPanel
              contactId={contactId}
              // `canAdopt` is the GRANT's answer, not "have we loaded it yet".
              // Conflating the two would make the panel say "the owner did not
              // allow adopting" for the half-second before the inventory lands
              // — a false statement about someone else's decision.
              access={{
                source: detail.access.source,
                ownerOrgName: detail.access.ownerOrgName,
                canAdopt: detail.access.canAdopt,
              }}
              viewingOrgName={viewingOrgName}
              enrichedAt={detail.enrichedAt}
              enrichmentStatus={detail.enrichmentStatus}
              onAdopt={shared && grant !== null ? () => setAdopting(true) : undefined}
            />
          </FeatureBoundary>

          <NotWired
            title="Per-field provenance"
            blockedOn="contacts.get has no fieldSources map; only email and phone rows carry a source today"
          >
            Fields without a recorded source render as <em>unverified</em> rather than claiming you
            typed them.
          </NotWired>
        </div>
      </FeatureBoundary>

      <FeatureBoundary feature="Timeline">
        <Card className="flex min-h-0 flex-col overflow-hidden p-0">
          <CardHeader className="border-b border-border">
            <CardTitle>Timeline</CardTitle>
          </CardHeader>
          <InteractionTimeline
            items={items}
            isLoading={timeline.isLoading}
            error={timeline.error}
            hasNextPage={timeline.hasNextPage}
            isFetchingNextPage={timeline.isFetchingNextPage}
            onLoadMore={() => void timeline.fetchNextPage()}
            focusId={focus ?? null}
          />
        </Card>
      </FeatureBoundary>

      <FeatureBoundary feature="Action rail">
        <div className="flex flex-col gap-4">
          <NotWired title="Next step" blockedOn="Wave 2 — Hermes drafts + channel picker" />
          <NotWired title="Promises & asks" blockedOn="Wave 1 track C — commitments table" />

          <FeatureBoundary feature="Sharing">
            <ContactSharingPanel contactId={contactId} contactName={name} canShare={!shared} />
          </FeatureBoundary>

          <FeatureBoundary feature="Privacy">
            <Card>
              <CardHeader>
                <CardTitle>Privacy</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {shared ? (
                  <p className="text-muted-foreground">
                    Suppressions belong to the workspace that owns the record. Set them on your own
                    copy after adopting.
                  </p>
                ) : (
                  <>
                    <SuppressionControls contactId={contactId} suppression={suppression} />
                    <p className="mt-2 text-xs text-muted-foreground">
                      Honoured everywhere without an override: the recorder refuses to arm, the
                      enrichment planner refuses before it estimates, and Hermes never drafts.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </FeatureBoundary>

          <NotWired title="Related people" blockedOn="pgvector similarity + company/event joins" />

          <Card>
            <CardHeader>
              <CardTitle>Record</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-1 text-xs text-muted-foreground">
              <span>Created {formatDate(detail.createdAt) ?? 'not shared'}</span>
              <span>Updated {formatDate(detail.updatedAt) ?? 'not shared'}</span>
              <span>
                Interactions{' '}
                {detail.interactionCount === null ? 'not shared' : detail.interactionCount}
              </span>
              {detail.adoptedFromShareId === null ? null : (
                <Badge variant="muted" className="mt-1 w-fit">
                  adopted from a grant
                </Badge>
              )}
              <Button
                size="sm"
                variant="outline"
                className="mt-2 w-fit"
                onClick={() => void timeline.refetch()}
              >
                Refresh timeline
              </Button>
            </CardContent>
          </Card>
        </div>
      </FeatureBoundary>

      <EditContactDialog contact={detail} open={editing} onOpenChange={setEditing} />

      {adopting && grant !== null ? (
        <AdoptDialog open onOpenChange={setAdopting} share={grant} />
      ) : null}
    </div>
  );
}
